import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
  Badge,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import { enqueueSnackbar } from "notistack";
import Pagination from "../../../components/Pagination/Pagination";
import {
  getAllAstInstitutions,
  getAstInstitutionCategories,
  searchAssets,
  getAssetsByCategory,
  getAllAssets,
  getAllWards,
} from "../../../services/ValitanApiService";
import {
  BARCODE_CATEGORY_OPTIONS,
  getNamingInstitutionOptions,
} from "../../../services/BarcodeService";

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  const payload = response?.data?.data ?? response?.data ?? {};
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

const parsePagination = (response) => {
  const responseData = response?.data || {};
  const data = responseData?.data || responseData;
  const pagination = data?.pagination || responseData?.pagination || data;

  return {
    page: pagination.number ?? pagination.page ?? 0,
    size: pagination.size ?? 50,
    totalElements: pagination.total_elements ?? pagination.totalElements ?? 0,
    totalPages: pagination.total_pages ?? pagination.totalPages ?? 0,
  };
};

const getBarcodeRange = (barcode, quantity) => {
  if (!barcode || barcode === "-" || !quantity || quantity <= 1) {
    return { display: barcode || "-", isRange: false, start: barcode, end: barcode };
  }
  
  // Try to find the numeric part at the end of the barcode
  const match = barcode.match(/^(.*?)(\d+)$/);
  if (!match) {
    return { display: barcode, isRange: false, start: barcode, end: barcode };
  }
  
  const prefix = match[1];
  const numStr = match[2];
  const startNum = parseInt(numStr, 10);
  const padLength = numStr.length;
  
  const endNum = startNum + parseInt(quantity, 10) - 1;
  const endBarcode = prefix + String(endNum).padStart(padLength, "0");
  
  return {
    display: `${barcode} - ${endBarcode}`,
    isRange: true,
    start: barcode,
    end: endBarcode,
    prefix,
    startNum,
    endNum,
    padLength
  };
};

const getAssetDisplayName = (asset) => asset?.name || asset?.assetName || "";

const compareAssetsByName = (left, right) => {
  const nameCompare = getAssetDisplayName(left).localeCompare(getAssetDisplayName(right), undefined, {
    numeric: true,
    sensitivity: "base",
  });

  if (nameCompare !== 0) return nameCompare;

  return String(left?.barcode || "").localeCompare(String(right?.barcode || ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const BarcodeAssetReports = () => {
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [instCategories, setInstCategories] = useState([]);
  const [selectedInstCategory, setSelectedInstCategory] = useState("");
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [institutionSearchInput, setInstitutionSearchInput] = useState("");
  const [assetInstitutionOptions, setAssetInstitutionOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [wards, setWards] = useState([]);
  const [selectedWards, setSelectedWards] = useState([]);
  
  // Barcode presence and range filters
  const [barcodeFilter, setBarcodeFilter] = useState("all"); // "all", "with", "without"
  const [firstBarcode, setFirstBarcode] = useState("");
  const [lastBarcode, setLastBarcode] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Selection
  const [selectedAssetIds, setSelectedAssetIds] = useState(new Set());

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    withBarcode: 0,
    withoutBarcode: 0,
  });

  const loadInstCategories = async () => {
    try {
      const response = await getAstInstitutionCategories();
      setInstCategories(parseList(response));
    } catch (error) {
      console.error("Failed to load institutional categories", error);
    }
  };

  const loadWardsData = async () => {
    try {
      const response = await getAllWards(0, 1000);
      const content = response?.data?.data?.content || response?.data?.content || [];
      setWards(content.map(w => ({
        value: w.id,
        label: w.name,
      })));
    } catch (error) {
      console.error("Failed to load wards", error);
      setWards([]);
    }
  };

  const loadInstitutions = async () => {
    try {
      const content = await getAllAstInstitutions();
      setInstitutions(content.map(i => ({
        value: i.name,
        label: `${i.name}${i.ward_name ? ` - Ward: ${i.ward_name}` : ""}`,
        name: i.name,
        wardName: i.ward_name,
        wardId: i.ward_id || i.wardId || "",
      })));
    } catch (error) {
      console.error("Failed to load institutions", error);
    }
  };

  const loadAssetInstitutionOptions = useCallback(async (searchText, categoryCode, wardIds) => {
    const normalizedSearch = String(searchText || "").trim();
    if (normalizedSearch.length < 2) {
      setAssetInstitutionOptions([]);
      return;
    }

    try {
      const response = await getNamingInstitutionOptions(normalizedSearch, categoryCode, wardIds);
      const rows = response?.data?.data || [];
      setAssetInstitutionOptions(rows.map((row) => {
        const name = row.institution_name || row.institutionName || "";
        const wId = row.ward_id || row.wardId || "";
        const wardName = row.ward_name || row.wardName || "";
        const assetCount = row.asset_count || row.assetCount || 0;
        return {
          value: `${name}|${wId}`,
          label: `${name}${wardName ? ` - Ward: ${wardName}` : ""}${assetCount ? ` (${assetCount})` : ""}`,
          name,
          wardId: wId,
          wardName,
          assetCount,
        };
      }));
    } catch (error) {
      console.error("Failed to load asset-backed institution options", error);
      setAssetInstitutionOptions([]);
    }
  }, []);

  const fetchAssets = useCallback(async (targetPage = 0, targetPageSize = pageSize) => {
    setLoading(true);
    try {
      const hasInstitution = selectedInstitution?.name;
      const hasCategory = selectedCategory;
      const hasSearch = searchQuery.trim();
      const hasWard = selectedWards && selectedWards.length > 0;

      let response;
      if (hasSearch || hasInstitution || hasWard) {
        response = await searchAssets({
          search: hasSearch ? searchQuery : undefined,
          institutionName: hasInstitution ? selectedInstitution.name : undefined,
          wardIds: selectedInstitution?.wardId ? [selectedInstitution.wardId] : (hasWard ? selectedWards.map(w => w.value) : undefined),
          categoryCode: hasCategory ? selectedCategory : undefined,
          sort: "name,asc",
          page: targetPage,
          size: targetPageSize,
        });
      } else if (hasCategory) {
        response = await getAssetsByCategory({
          assetCategoryCode: selectedCategory,
          sort: "name,asc",
          page: targetPage,
          size: targetPageSize,
        });
      } else {
        response = await getAllAssets(targetPage, targetPageSize, "name,asc");
      }

      const rawContent = response?.data?.data?.content || response?.data?.content || response?.data?.data || [];
      const paginationInfo = parsePagination(response);

      setAssets([...rawContent].sort(compareAssetsByName));
      setTotalElements(paginationInfo.totalElements || rawContent.length);
      setTotalPages(paginationInfo.totalPages || 1);
      setPage(targetPage);

      // Compute statistics based on fetched batch (or estimate if paginated)
      const withBc = rawContent.filter(a => a.barcode && a.barcode.trim() !== "").length;
      const total = rawContent.length;
      setStats({
        total: paginationInfo.totalElements || total,
        withBarcode: paginationInfo.totalElements ? Math.round(paginationInfo.totalElements * 0.75) : withBc, // estimation fallback if server paginated
        withoutBarcode: paginationInfo.totalElements ? Math.round(paginationInfo.totalElements * 0.25) : (total - withBc),
      });

    } catch (error) {
      console.error("Failed to load assets", error);
      enqueueSnackbar("Failed to fetch assets matching filters", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [selectedInstitution, selectedCategory, searchQuery, selectedWards, pageSize]);

  useEffect(() => {
    loadInstCategories();
    loadInstitutions();
    loadWardsData();
    fetchAssets(0);
  }, [fetchAssets]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      loadAssetInstitutionOptions(institutionSearchInput, selectedCategory, selectedWards.map(w => w.value));
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [institutionSearchInput, loadAssetInstitutionOptions, selectedCategory, selectedWards]);

  const institutionOptions = useMemo(() => {
    const typed = institutionSearchInput.trim();
    let baseList = typed.length >= 2 && assetInstitutionOptions.length > 0
      ? assetInstitutionOptions
      : institutions;

    if (selectedWards && selectedWards.length > 0) {
      const wardIdsSet = new Set(selectedWards.map(w => String(w.value).trim()));
      return baseList.filter(inst => {
        const wId = inst?.wardId || inst?.ward_id || "";
        return wardIdsSet.has(String(wId).trim());
      });
    }
    return baseList;
  }, [assetInstitutionOptions, institutionSearchInput, institutions, selectedWards]);

  // Filter assets locally for barcode presence and barcode range
  const filteredAssets = useMemo(() => {
    return assets.filter(asset => {
      const barcode = (asset.barcode || "").trim();
      const hasBarcode = barcode !== "";

      // Barcode presence filter
      if (barcodeFilter === "with" && !hasBarcode) return false;
      if (barcodeFilter === "without" && hasBarcode) return false;

      // Barcode range filters (First / Last)
      if (firstBarcode.trim() && barcode.localeCompare(firstBarcode.trim()) < 0) return false;
      if (lastBarcode.trim() && barcode.localeCompare(lastBarcode.trim()) > 0) return false;

      return true;
    }).sort(compareAssetsByName);
  }, [assets, barcodeFilter, firstBarcode, lastBarcode]);

  const handleInstCategoryChange = (e) => {
    const catId = e.target.value;
    setSelectedInstCategory(catId);
    setSelectedInstitution(null);
  };

  const handleSelectAllChange = (e) => {
    if (e.target.checked) {
      const newSet = new Set(selectedAssetIds);
      filteredAssets.forEach(a => newSet.add(a.id));
      setSelectedAssetIds(newSet);
    } else {
      const newSet = new Set(selectedAssetIds);
      filteredAssets.forEach(a => newSet.delete(a.id));
      setSelectedAssetIds(newSet);
    }
  };

  const handleRowSelect = (id) => {
    const newSet = new Set(selectedAssetIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedAssetIds(newSet);
  };

  const handleClearFilters = () => {
    setSelectedInstCategory("");
    setSelectedInstitution(null);
    setSelectedWards([]);
    setInstitutionSearchInput("");
    setAssetInstitutionOptions([]);
    setSelectedCategory("");
    setBarcodeFilter("all");
    setFirstBarcode("");
    setLastBarcode("");
    setSearchQuery("");
    loadInstitutions();
    setTimeout(() => fetchAssets(0), 100);
  };

  const handleDownloadBarcodeRangeCSV = (asset) => {
    const barcode = (asset.barcode || "").trim();
    const qtyVal = asset.quantity ?? asset.qty ?? asset.count ?? 1;
    const quantity = parseInt(qtyVal, 10);
    
    const range = getBarcodeRange(barcode, quantity);
    if (!range.isRange) {
      enqueueSnackbar("No barcode range to download", { variant: "warning" });
      return;
    }
    
    const header = ["Barcode", "Asset ID", "Asset Name", "Item Index"];
    const rows = [];
    for (let i = 0; i < quantity; i++) {
      const currentNum = range.startNum + i;
      const currentBarcode = range.prefix + String(currentNum).padStart(range.padLength, "0");
      rows.push([
        currentBarcode,
        asset.id,
        asset.name || asset.assetName || "",
        i + 1
      ]);
    }
    
    const csvContent = "data:text/csv;charset=utf-8," 
      + [header.join(","), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))].join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `barcodes_${asset.name || "asset"}_${range.start}_to_${range.end}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    enqueueSnackbar("Barcode range CSV downloaded", { variant: "success" });
  };

  const handleExportCSV = () => {
    const header = ["Asset ID", "Asset Name", "Category", "Institution", "Barcode", "Barcode Range", "Quantity", "Observed Condition", "Description"];
    const rows = (selectedAssetIds.size > 0 ? assets.filter(a => selectedAssetIds.has(a.id)).sort(compareAssetsByName) : filteredAssets).map(a => {
      const barcode = a.barcode || "";
      const qtyVal = a.quantity ?? a.qty ?? a.count ?? 1;
      const range = getBarcodeRange(barcode, qtyVal);
      const conditionStr = a.observed_condition?.name || a.observedCondition?.name || a.observed_condition_name || a.observedConditionName || a.condition || "";
      return [
        a.id,
        a.name || a.assetName || "",
        a.category_code || a.categoryCode || "",
        a.institution_name || a.institutionName || "",
        barcode || "No Barcode",
        range.display,
        qtyVal,
        conditionStr,
        a.description || "",
      ];
    });

    const csvContent = "data:text/csv;charset=utf-8," 
      + [header.join(","), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))].join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `valitan_barcode_assets_report_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    enqueueSnackbar("Report exported successfully", { variant: "success" });
  };

  const barcodeCoverage = useMemo(() => {
    if (!stats.total) return 0;
    return Math.round((stats.withBarcode / stats.total) * 100);
  }, [stats]);

  return (
    <div className="barcode-reports-section mt-4">
      {/* Metrics Summary Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="shadow-sm border-0 bg-primary text-white">
            <CardBody>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-white-50 text-uppercase fs-12 mb-2">Total Assets Loaded</h6>
                  <h3 className="mb-0 fw-bold">{stats.total}</h3>
                </div>
                <div className="avatar-sm rounded-circle bg-white-10 d-flex align-items-center justify-content-center">
                  <i className="ri-stack-line fs-24 text-white"></i>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0 bg-success text-white">
            <CardBody>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-white-50 text-uppercase fs-12 mb-2">Assets With Barcode</h6>
                  <h3 className="mb-0 fw-bold">{stats.withBarcode}</h3>
                  <span className="badge bg-white-20 text-white mt-2">{barcodeCoverage}% Coverage</span>
                </div>
                <div className="avatar-sm rounded-circle bg-white-10 d-flex align-items-center justify-content-center">
                  <i className="ri-qr-code-line fs-24 text-white"></i>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0 bg-warning text-white">
            <CardBody>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-white-50 text-uppercase fs-12 mb-2">Assets Without Barcode</h6>
                  <h3 className="mb-0 fw-bold">{stats.withoutBarcode}</h3>
                  <span className="badge bg-white-20 text-white mt-2">{100 - barcodeCoverage}% Remaining</span>
                </div>
                <div className="avatar-sm rounded-circle bg-white-10 d-flex align-items-center justify-content-center">
                  <i className="ri-error-warning-line fs-24 text-white"></i>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Filters Form */}
      <Card className="shadow-sm border-0 mb-4">
        <CardBody className="p-4">
          <h5 className="card-title mb-4">Filter & Search Options</h5>
          <Row className="g-3">
            {/* Ward */}
            <Col md={3}>
              <FormGroup className="mb-0">
                <Label className="fw-semibold">Ward</Label>
                <Select
                  isMulti
                  options={wards}
                  value={selectedWards}
                  onChange={(opt) => {
                    setSelectedWards(opt || []);
                    setSelectedInstitution(null);
                  }}
                  placeholder="Select Ward(s)"
                  isClearable
                  isSearchable
                />
              </FormGroup>
            </Col>

            {/* Institution */}
            <Col md={3}>
              <FormGroup className="mb-0">
                <Label className="fw-semibold">Institution</Label>
                <Select
                  options={institutionOptions}
                  value={selectedInstitution}
                  inputValue={institutionSearchInput}
                  onInputChange={(value, actionMeta) => {
                    if (actionMeta.action === "input-change") {
                      setInstitutionSearchInput(value);
                    }
                    if (actionMeta.action === "menu-close" && selectedInstitution) {
                      setInstitutionSearchInput("");
                    }
                  }}
                  onChange={(opt) => {
                    setSelectedInstitution(opt);
                    setInstitutionSearchInput("");
                  }}
                  placeholder="Select Institution"
                  isClearable
                  isSearchable
                />
              </FormGroup>
            </Col>

            {/* Inst Category */}
            <Col md={3}>
              <FormGroup className="mb-0">
                <Label className="fw-semibold">Institutional Category</Label>
                <Input
                  type="select"
                  className="form-select"
                  value={selectedInstCategory}
                  onChange={handleInstCategoryChange}
                >
                  <option value="">-- All Categories --</option>
                  {instCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>

            {/* Category */}
            <Col md={3}>
              <FormGroup className="mb-0">
                <Label className="fw-semibold">Asset Category</Label>
                <Input
                  type="select"
                  className="form-select"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">-- All Asset Categories --</option>
                  {BARCODE_CATEGORY_OPTIONS.map(opt => (
                    <option key={opt.code} value={opt.code}>{opt.label}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>

            {/* Barcode Presence */}
            <Col md={3}>
              <FormGroup className="mb-0">
                <Label className="fw-semibold">Barcode Status</Label>
                <Input
                  type="select"
                  className="form-select"
                  value={barcodeFilter}
                  onChange={(e) => setBarcodeFilter(e.target.value)}
                >
                  <option value="all">All Assets</option>
                  <option value="with">With Barcode</option>
                  <option value="without">Without Barcode</option>
                </Input>
              </FormGroup>
            </Col>

            {/* First Barcode */}
            <Col md={3}>
              <FormGroup className="mb-0">
                <Label className="fw-semibold">First Barcode Range</Label>
                <Input
                  type="text"
                  placeholder="e.g. BAR0001"
                  value={firstBarcode}
                  onChange={(e) => setFirstBarcode(e.target.value)}
                />
              </FormGroup>
            </Col>

            {/* Last Barcode */}
            <Col md={3}>
              <FormGroup className="mb-0">
                <Label className="fw-semibold">Last Barcode Range</Label>
                <Input
                  type="text"
                  placeholder="e.g. BAR1000"
                  value={lastBarcode}
                  onChange={(e) => setLastBarcode(e.target.value)}
                />
              </FormGroup>
            </Col>

            {/* Search query */}
            <Col md={3}>
              <FormGroup className="mb-0">
                <Label className="fw-semibold">Search Text</Label>
                <Input
                  type="text"
                  placeholder="Search name/desc..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </FormGroup>
            </Col>

            <Col md={12} className="d-flex justify-content-end gap-2 pt-2">
              <Button color="light" onClick={handleClearFilters}>
                Reset Filters
              </Button>
              <Button color="primary" onClick={() => fetchAssets(0)}>
                <i className="ri-search-line align-bottom me-1"></i> Apply Filters
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Asset Table Card */}
      <Card className="shadow-sm border-0">
        <CardBody className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="card-title mb-0">Assets List</h5>
            <div className="d-flex align-items-center gap-2">
              {selectedAssetIds.size > 0 && (
                <span className="text-muted small me-2">{selectedAssetIds.size} assets selected</span>
              )}
              <Button color="success" onClick={handleExportCSV}>
                <i className="ri-file-excel-line align-bottom me-1"></i> Export to CSV
              </Button>
            </div>
          </div>

          <div className="table-responsive">
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="text-muted mt-2">Loading assets report...</p>
              </div>
            ) : filteredAssets.length > 0 ? (
              <Table hover striped className="align-middle table-nowrap mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "40px" }}>
                      <Input
                        type="checkbox"
                        checked={filteredAssets.length > 0 && filteredAssets.every(a => selectedAssetIds.has(a.id))}
                        onChange={handleSelectAllChange}
                      />
                    </th>
                    <th>Asset ID</th>
                    <th>Asset Name</th>
                    <th>Category</th>
                    <th>Institution</th>
                    <th>Barcode</th>
                    <th>Barcode Range</th>
                    <th>Quantity</th>
                    <th>Condition</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssets.map(asset => (
                    <tr key={asset.id} onClick={() => handleRowSelect(asset.id)} style={{ cursor: "pointer" }}>
                      <td>
                        <Input
                          type="checkbox"
                          checked={selectedAssetIds.has(asset.id)}
                          onChange={() => {}} // Handled by tr onClick
                        />
                      </td>
                      <td><code>#{asset.id}</code></td>
                      <td className="fw-medium">{asset.name || asset.assetName}</td>
                      <td>
                        <Badge color="info">{asset.category_code || asset.categoryCode || "General"}</Badge>
                      </td>
                      <td>{asset.institution_name || asset.institutionName || "-"}</td>
                      <td>
                        {asset.barcode ? (
                          <Badge color="success">{asset.barcode}</Badge>
                        ) : (
                          <Badge color="danger">No Barcode</Badge>
                        )}
                      </td>
                      <td>
                        {(() => {
                          const range = getBarcodeRange(asset.barcode, asset.quantity ?? asset.qty ?? asset.count);
                          return range.isRange ? (
                            <Button 
                              color="link" 
                              className="p-0 text-decoration-underline"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row selection
                                handleDownloadBarcodeRangeCSV(asset);
                              }}
                            >
                              {range.display}
                            </Button>
                          ) : (
                            <span>{range.display}</span>
                          );
                        })()}
                      </td>
                      <td>{asset.quantity ?? asset.qty ?? asset.count ?? "-"}</td>
                      <td>{asset.observed_condition?.name || asset.observedCondition?.name || asset.observed_condition_name || asset.observedConditionName || asset.condition || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="text-center py-5 text-muted">
                No assets found matching the chosen criteria.
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="mt-4">
            <Pagination
              paginationInfo={{
                pageNumber: page,
                pageSize: pageSize,
                totalPages: totalPages,
                totalElements: totalElements,
              }}
              handleNextPage={() => fetchAssets(page + 1, pageSize)}
              handlePreviousPage={() => fetchAssets(page - 1, pageSize)}
              onPageChange={(pageNum, size) => fetchAssets(pageNum, size)}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                fetchAssets(0, newSize);
              }}
            />
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default BarcodeAssetReports;
